import React from "react";
import { formatNumberToLocal, toastError, toastSuccess } from "../../helpers";
import { Button, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { usePurchaseHeliorOneMutation } from "../../app/features/apiSlice";
import { BANK_PAYMENT_METHODS, CURRENCY_SYMBOLS } from "../../Constants";

const HOneConfirmCard = ({ paymentData }) => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const navigate = useNavigate();

  const currencySymbol =
    CURRENCY_SYMBOLS[paymentData.currency] ?? paymentData.currency;
  const conv = (v) =>
    formatNumberToLocal((v ?? 0) * paymentData.exchangeRate) || 0;

  const [purchaseHeliorOne, { isLoading }] = usePurchaseHeliorOneMutation();
  const handleConfirmPurchase = async () => {
    try {
      const ingots = paymentData.ingots.filter((item) => item.quantity > 0);
      const data = {
        clientId: "",
        duration: paymentData.duration.toString(),
        ingots,
        contractDate: Date.now(),
        type: paymentData.type === "custody" ? "case" : "home_delivery",
        acceptTNC: paymentData.acceptTNC || paymentData.O01,
        O01: paymentData.O01,
        O02: paymentData.O02,
        O03: paymentData.O03,
        O04: paymentData.O04,
        C01: paymentData.C01,
        C02: paymentData.C02,
        C03: paymentData.C03,
        B01: paymentData.B01,
        B02O: paymentData.B02O,
        B03: paymentData.B03,
        paymentMethod: paymentData.paymentMethod,
        currency: paymentData.currency,
        language: lang,
      };
      const response = await purchaseHeliorOne({
        data,
        planId: paymentData?.planId,
      });
      if (response?.error) {
        toastError(response, t("error.serverNotResponding"));
      } else {
        toastSuccess(response?.data?.message);
        navigate(`/${lang}/payment-success`);
      }
    } catch (err) {
      toastError(err, t("error.serverNotResponding"));
    }
  };
  return (
    <div className="px-2 px-sm-3 px-lg-5 py-2 w-100 h-one-confirm">
      <h6 className="mb-3">{t("summary")}</h6>

      {/* Only table will scroll horizontally */}
      <div className="table-responsive w-100 payment-table-wrapper">
        <Table className="payment-table mb-0">
          <thead>
            <tr>
              <th></th>
              <th>{t("amount")}</th>

              <th>
                {t("guaranteeMargin2")} {paymentData?.guaranteeMargin}%
              </th>

              <th>
                {t("guaranteeMargin")} (
                {paymentData.paymentMethod && (
                  <>
                    {BANK_PAYMENT_METHODS.includes(paymentData.paymentMethod)
                      ? paymentData.txBankFee
                      : paymentData.txCryptoFee}
                    %)
                  </>
                )}
              </th>

              <th>{t("total")}</th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>{t("goldCost")}</td>
              <td>{conv(paymentData.goldCost) || 0}</td>
              <td>
                {conv(paymentData.goldPriceWithGuaranteeMargin)}
              </td>
              <td>{conv(paymentData.goldPriceWithMargin) || 0}</td>
              <td>
                {conv(
                  paymentData.goldCost +
                    paymentData.goldPriceWithMargin +
                    paymentData.goldPriceWithGuaranteeMargin
                ) || 0}
              </td>
            </tr>

            <tr>
              <td>
                {t("spreadCost")}{" "}
                {paymentData.spreadValue > 0 && (
                  <span>({paymentData.spreadValue * 100}%)</span>
                )}
              </td>

              <td>{conv(paymentData.spreadCost)}</td>

              <td>
                {conv(paymentData.costSpreadWithGuaranteeMargin)}
              </td>

              <td>
                {conv(paymentData.costSpreadWithMargin)}
              </td>

              <td>
                {conv(
                  paymentData.spreadCost +
                    paymentData.costSpreadWithMargin +
                    paymentData.costSpreadWithGuaranteeMargin
                )}
              </td>
            </tr>

            <tr>
              <td>
                {t("storageCost")}{" "}
                {paymentData.storageValue > 0 && (
                  <span>({paymentData.storageValue * 100}%)</span>
                )}
              </td>

              <td>{conv(paymentData.storageCost)}</td>

              <td>
                {conv(paymentData.storageCostWithGuaranteeMargin)}
              </td>

              <td>
                {conv(paymentData.storageCostWithMargin)}
              </td>

              <td>
                {conv(
                  paymentData.storageCost +
                    paymentData.storageCostWithMargin +
                    paymentData.storageCostWithGuaranteeMargin
                )}
              </td>
            </tr>

            <tr>
              <td>{t("manufacturingAmount")}</td>
              <td>{conv(paymentData.totalManufacturingCost)}</td>
              <td>-</td>
              <td>-</td>
              <td>{conv(paymentData.totalManufacturingCost)}</td>
            </tr>

            <tr>
              <td>{t("total")}</td>

              <td>{conv(paymentData.totalCost)}</td>

              <td>
                {conv(
                  paymentData.total_payment_with_guarantee_margin
                )}
              </td>

              <td>
                {conv(paymentData.total_payment_with_margin)}
              </td>

              <td>
                {conv(
                  paymentData.totalCost +
                    paymentData.total_payment_with_margin +
                    paymentData.total_payment_with_guarantee_margin
                )}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>

      {/* Bottom Details */}
      <div className="px-1 w-100">
        <p className="d-flex justify-content-between align-items-start gap-2 mt-3 mb-1 text-uppercase">
          <span>{t("totalGrams")}</span>

          <span className="text-end text-break">
            {paymentData.totalGrams}gr
          </span>
        </p>

        <p className="d-flex justify-content-between align-items-start gap-2 mb-1 text-uppercase">
          <span>{t("paymentMethod")}</span>

          <span className="text-end text-break">
            {paymentData.paymentMethod}
          </span>
        </p>

        {paymentData.stampDuty > 0 && (
          <>
            <p className="d-flex justify-content-between align-items-start gap-2 mb-1 text-uppercase">
              <span>{t("price")}</span>

              <span className="text-end text-break">
                {currencySymbol}{" "}
                {conv(paymentData.bank_transfer) || 0}
              </span>
            </p>

            <p className="d-flex justify-content-between align-items-start gap-2 mb-1 text-uppercase">
              <span>{t("stampDuty")}</span>

              <span className="text-end text-break">
                {currencySymbol}{" "}
                {conv(paymentData.stampDuty) || 0}
              </span>
            </p>
          </>
        )}

        <hr />

        <p className="d-flex justify-content-between align-items-start gap-2 mb-4 text-uppercase">
          <span>{t("totalPayment")}</span>

          <span className="text-end text-break">
            {currencySymbol}{" "}
            {conv(
              paymentData.bank_transfer +
                paymentData.stampDuty
            )}
          </span>
        </p>

        <Button
          variant="primary-outline"
          className="w-100 d-flex align-items-center justify-content-center"
          onClick={handleConfirmPurchase}
        >
          {t("xOneTncFlags.confirmOrderWithObligation") || t("confirmPurchase")}

          {isLoading && (
            <Spinner
              size="sm"
              className="ms-2"
              animation="grow"
            />
          )}
        </Button>
      </div>
    </div>
  );
};

export default HOneConfirmCard;
